import PropTypes from "prop-types";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { getSubjectConfig } from "../../utils/subjectConfig";
import { getGradeLabel } from "../../utils/curriculumConfig";
import { compareGrades } from "../../utils/gradeSort";
import { useState } from "react";

/**
 * A subject-centric filter component that shows each subject with its available grades.
 * Only ONE subject-grade combination can be selected at a time.
 * Selecting a new subject or grade deselects the previous selection.
 */
const SubjectGradeFilter = ({
  subjectGradeMap,
  selectedSubjects,
  selectedGrades,
  onSubjectsChange,
  onGradesChange,
  disabled,
  curriculum,
}) => {
  const gradeLabel = getGradeLabel(curriculum);
  const [expandedSubjects, setExpandedSubjects] = useState(() => new Set());

  const sortGrades = (grades) => [...grades].sort(compareGrades);

  const toggleSubjectExpand = (subject) => {
    const newExpanded = new Set(expandedSubjects);
    if (newExpanded.has(subject)) {
      newExpanded.delete(subject);
    } else {
      newExpanded.add(subject);
    }
    setExpandedSubjects(newExpanded);
  };

  const handleGradeToggle = (subject, grade) => {
    if (disabled) {
      return;
    }

    // If clicking on a grade from a different subject, switch to that subject
    if (!selectedSubjects.includes(subject)) {
      onSubjectsChange([subject]);
      onGradesChange([grade]);
      return;
    }

    // Toggle grade within the current subject - only one grade can be selected
    if (selectedGrades.includes(grade)) {
      // Deselecting the current grade
      onGradesChange([]);
    } else {
      // Selecting a new grade - replace any previous selection
      onGradesChange([grade]);
    }
  };

  const subjects = Object.keys(subjectGradeMap);

  // Get selected grade for display (if any)
  const getSelectedGrade = (subject) => {
    const isThisSubjectSelected = selectedSubjects.includes(subject);
    if (!isThisSubjectSelected || selectedGrades.length === 0) {
      return null;
    }
    return selectedGrades[0];
  };

  return (
    <div className="space-y-3">
      {subjects.map((subject) => {
        const subjectConfig = getSubjectConfig(subject);
        const SubjectIcon = subjectConfig?.icon;
        const isSubjectSelected = selectedSubjects.includes(subject);
        const isExpanded = expandedSubjects.has(subject);
        const availableGrades = sortGrades(subjectGradeMap[subject] || []);
        const selectedGrade = getSelectedGrade(subject);

        return (
          <div
            key={subject}
            className={`overflow-hidden rounded-xl border-2 transition-all duration-200 ${
              isSubjectSelected
                ? `${subjectConfig.borderColor} ${subjectConfig.bgColor}`
                : "border-gray-200 bg-white"
            }`}
          >
            {/* Subject Header — click anywhere to expand/collapse */}
            <button
              type="button"
              onClick={() => toggleSubjectExpand(subject)}
              className={`flex w-full items-center justify-between px-4 py-3 text-left transition-colors ${
                isSubjectSelected
                  ? `${subjectConfig.lightBgColor}`
                  : "bg-gray-50 hover:bg-gray-100"
              }`}
            >
              <div className="flex items-center gap-3">
                {/* Subject Icon & Name */}
                {SubjectIcon && (
                  <div
                    className={`flex size-7 items-center justify-center rounded-lg ${subjectConfig.bgColor}`}
                  >
                    <SubjectIcon className={`size-4 ${subjectConfig.color}`} />
                  </div>
                )}
                <span className="text-base font-semibold text-gray-900">
                  {subject}
                </span>

                {/* Grade Range Badge */}
                <span className="rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                  {gradeLabel}s {availableGrades[0]}–
                  {availableGrades[availableGrades.length - 1]}
                </span>
              </div>

              <div className="flex items-center gap-3">
                {/* Selected Grade Display */}
                {isSubjectSelected && selectedGrade && (
                  <span className="rounded-full bg-primary-100 px-3 py-1 text-sm font-semibold text-primary-700">
                    {gradeLabel} {selectedGrade}
                  </span>
                )}

                {/* Expand/Collapse Chevron */}
                {isExpanded ? (
                  <FiChevronUp className="size-5 text-gray-500" />
                ) : (
                  <FiChevronDown className="size-5 text-gray-500" />
                )}
              </div>
            </button>

            {/* Grades Grid */}
            {isExpanded && (
              <div className="border-t border-gray-200/50 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  {/* Individual Grade Chips */}
                  {availableGrades.map((grade) => {
                    // Only show as selected if this subject is selected
                    const isGradeSelected =
                      isSubjectSelected && selectedGrades.includes(grade);
                    return (
                      <button
                        key={grade}
                        type="button"
                        onClick={() => handleGradeToggle(subject, grade)}
                        disabled={disabled}
                        className={`flex min-w-10 items-center justify-center rounded-lg border-2 px-3 py-1.5 text-sm font-medium transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                          isGradeSelected
                            ? `${subjectConfig.borderColor} ${subjectConfig.lightBgColor} ${subjectConfig.color}`
                            : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {grade === "K" ? "K" : grade}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {subjects.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-8 text-center">
          <p className="text-sm text-gray-500">No datasets available</p>
        </div>
      )}
    </div>
  );
};

SubjectGradeFilter.propTypes = {
  subjectGradeMap: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))
    .isRequired,
  selectedSubjects: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedGrades: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSubjectsChange: PropTypes.func.isRequired,
  onGradesChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  curriculum: PropTypes.string,
};

SubjectGradeFilter.defaultProps = {
  disabled: false,
  curriculum: "",
};

export default SubjectGradeFilter;
